import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { seg, ease, lerp } from './motionKit';

/**
 * "The Build" — a pure-particle world for Lab R2 (The Studio).
 *
 * ONE particle system (24k GPU-morphed points) is the whole scene. Life cycle:
 *
 *   motes    (hero)     calm drifting field, gathers as the work approaches
 *   plane    (zero)     particles snap to a breathing lattice
 *   monument (build)    edge-weighted particle architecture
 *   website  (yours)    the camera dives in and the particles reorganize into
 *                       a website drawn in the same pixel style — page frame,
 *                       nav, headline, CTA pill, cards
 *   the tour (details)  the camera visits each component; the active one
 *                       flares while the rest dims — a living demo
 *   recede   (services) the site parks deep in the background; embers rise
 *   ring     (contact)  everything converges into a slow-rotating halo
 *
 * All progress is smoothed per-frame; bloom breathes with the story.
 */

const GREY = new THREE.Color('#9aa08f');
const N = 24000;

const BOXES = [
  [4.6, 0.7, 3.0, 0, -0.65, 0],
  [3.7, 1.5, 2.3, -0.15, 0.45, 0],
  [2.7, 2.3, 1.8, 0.1, 2.35, 0.05],
  [1.15, 1.05, 1.15, 1.95, 0.15, 0.6],
  [0.95, 0.8, 0.95, -2.1, 0.05, -0.5],
];

// Component ids for the site formation / tour focus
const COMP = { FRAME: 4, NAV: 0, HERO: 1, CTA: 2, CARDS: 3, AMBIENT: 5 };

// Tour stops on the story timeline: [start, width], component focused
export const TOUR_STOPS = [
  { s: 0.542, w: 0.052, comp: COMP.NAV },
  { s: 0.6, w: 0.052, comp: COMP.HERO },
  { s: 0.658, w: 0.052, comp: COMP.CTA },
  { s: 0.716, w: 0.052, comp: COMP.CARDS },
];

// World-space bounds of each toured component (z = 1 plane) — projected for
// the DOM "lens" panels that resolve the dust into real UI
const COMP_RECTS = [
  { x0: -2.05, x1: 2.3, y0: 3.02, y1: 3.32 },
  { x0: -2.0, x1: 1.4, y0: 2.1, y1: 2.9 },
  { x0: -2.0, x1: -0.12, y0: 1.5, y1: 1.9 },
  { x0: -2.05, x1: 2.25, y0: 0.6, y1: 1.34 },
];

const CAM = [
  { p: 0.0, pos: [0, 0.3, 17.0], look: [0, 0.5, 0] },
  { p: 0.1, pos: [0, 0.4, 15.5], look: [0, 0.4, 0] },
  { p: 0.24, pos: [0, 5.2, 8.2], look: [0, -0.9, 0] },
  { p: 0.34, pos: [7.4, 4.0, 10.6], look: [0, 1.2, 0] },
  { p: 0.43, pos: [0, 2.6, 10.2], look: [0, 1.3, 0] },
  { p: 0.475, pos: [0.1, 2.4, 7.6], look: [0.1, 2.2, 1] },
  { p: 0.522, pos: [0.1, 2.1, 6.2], look: [0.1, 2.1, 1] },
  { p: 0.568, pos: [0.1, 2.85, 5.3], look: [0.1, 2.95, 1] },
  { p: 0.626, pos: [0.0, 2.35, 5.3], look: [0.0, 2.4, 1] },
  { p: 0.684, pos: [-0.7, 1.85, 4.9], look: [-0.85, 1.8, 1] },
  { p: 0.742, pos: [0.1, 1.15, 5.3], look: [0.1, 1.1, 1] },
  { p: 0.772, pos: [0.1, 2.1, 7.0], look: [0.1, 2.1, 1] },
  { p: 0.86, pos: [0, 1.8, 12.0], look: [0, 1.2, -1.0] },
  { p: 1.0, pos: [0, 1.4, 10.8], look: [0, 1.15, -2.0] },
];

const VERT = `
attribute vec3 aGrid;
attribute vec3 aStruct;
attribute vec3 aSite;
attribute vec3 aRing;
attribute float aSeed;
attribute float aSize;
attribute float aBright;
attribute float aBrightSite;
attribute float aAcc;
attribute float aComp;
attribute float aHalo;
uniform float uTime;
uniform float uSize;
uniform float uVel;
uniform float uGather;
uniform float uGrid;
uniform float uStruct;
uniform float uSite;
uniform float uRecede;
uniform float uRing;
uniform float uRipple;
uniform float uEmber;
uniform float uHalo;
uniform float uFocusComp;
uniform float uFocusAmt;
uniform vec3 uAnchor;
varying float vAlpha;
varying float vAcc;

void main() {
  vec3 p = position;
  float dAmp = (1.0 - uGrid) * 0.55 + uRing * 0.28;
  p.x += sin(uTime * 0.32 + aSeed * 37.0 + position.y * 0.4) * dAmp;
  p.y += cos(uTime * 0.27 + aSeed * 23.0 + position.x * 0.3) * dAmp * 0.8;
  p.z += sin(uTime * 0.29 + aSeed * 51.0) * dAmp;
  p *= (1.0 - 0.12 * uGather);

  float hb = aHalo * uHalo;
  if (hb > 0.001) {
    float hAng = aSeed * 6.2831 + uTime * 0.22;
    vec3 hp = uAnchor + vec3(
      cos(hAng) * (0.95 + 0.2 * sin(uTime * 0.7 + aSeed * 21.0)),
      sin(hAng) * 0.42,
      (aSeed - 0.5) * 0.55
    );
    p = mix(p, hp, hb);
  }

  vec3 g = aGrid;
  g.y += sin(aGrid.x * 0.7 + uTime * 0.7) * cos(aGrid.z * 0.6 + uTime * 0.5) * 0.09 * uRipple;
  p = mix(p, g, uGrid);

  p = mix(p, aStruct, uStruct);

  // the zoom: the monument reorganizes into the pixel website
  p = mix(p, aSite, uSite);

  // the site dissolves back into faint dust as services arrives; embers rise
  vec3 r = position * 1.3 + vec3(0.0, 0.6, -3.5);
  p = mix(p, r, uRecede);
  if (aSeed < 0.12) {
    float cycle = fract(uTime * 0.04 + aSeed * 8.0);
    p.y += cycle * 9.0 * uEmber;
    p.x += sin(uTime * 0.5 + aSeed * 40.0) * 0.6 * uEmber;
  }

  // the halo: upright, facing the viewer, particles orbiting in-plane,
  // with a gentle fixed tilt so it always reads as an aperture
  float ca = cos(uTime * 0.08);
  float sa = sin(uTime * 0.08);
  vec3 q = vec3(aRing.x * ca - aRing.y * sa, aRing.x * sa + aRing.y * ca, aRing.z);
  float qy = q.y * 0.95 - q.z * 0.31;
  q.z = q.y * 0.31 + q.z * 0.95;
  q.y = qy;
  q += vec3(0.0, 1.15, -2.0);
  p = mix(p, q, uRing);

  // the tour: the focused component flares, the rest holds its breath
  float match = (uFocusComp > -0.5 && abs(aComp - uFocusComp) < 0.5) ? 1.0 : 0.0;
  float focusIn = match * uFocusAmt * uSite;
  float focusOut = (1.0 - match) * uFocusAmt * uSite;

  float b = mix(aBright, aBrightSite, uSite);
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  float tw = 0.72 + 0.28 * sin(uTime * (0.7 + aSeed) + aSeed * 6.2831);
  gl_PointSize = uSize * aSize * (0.5 + 0.5 * b) * tw * (1.0 - 0.3 * uSite)
    * (1.0 + uVel * 0.6 + hb * 0.8 + focusIn * 0.55) * (300.0 / -mv.z);
  gl_Position = projectionMatrix * mv;
  vAlpha = (0.4 + 0.6 * b) * (1.0 + hb * 0.7 + focusIn * 0.7) * (1.0 - focusOut * 0.55);
  vAcc = aAcc * uSite;
}
`;

const FRAG = `
uniform vec3 uColor;
uniform vec3 uAccent;
uniform float uOpacity;
varying float vAlpha;
varying float vAcc;
void main() {
  vec2 uv = gl_PointCoord - 0.5;
  float d = length(uv);
  float a = smoothstep(0.5, 0.06, d);
  vec3 c = mix(uColor, uAccent, vAcc * 0.85);
  gl_FragColor = vec4(c, a * uOpacity * vAlpha);
}
`;

const THEMES = {
  dark: {
    bg: '#050507',
    base: new THREE.Color('#9aa08f'),
    accent: new THREE.Color('#B0FC38'),
    soft: new THREE.Color('#d9e0cf'),
    blending: THREE.AdditiveBlending,
    bloom: 1,
    threshold: 0.16,
    opacity: 1,
    size: 1,
  },
  light: {
    bg: '#F2EFE9',
    base: new THREE.Color('#6B675A'),
    accent: new THREE.Color('#3F6212'),
    soft: new THREE.Color('#57534A'),
    blending: THREE.NormalBlending,
    bloom: 0.12,
    threshold: 0.995,
    opacity: 1.45,
    size: 1.25,
  },
};

export function createStudioScene(canvas) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));

  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#050507');
  scene.fog = new THREE.FogExp2(0x050507, 0.02);

  const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 80);

  const chaos = new Float32Array(N * 3);
  const grid = new Float32Array(N * 3);
  const struct = new Float32Array(N * 3);
  const site = new Float32Array(N * 3);
  const ring = new Float32Array(N * 3);
  const seeds = new Float32Array(N);
  const sizes = new Float32Array(N);
  const bright = new Float32Array(N);
  const brightSite = new Float32Array(N);
  const acc = new Float32Array(N);
  const comp = new Float32Array(N);
  const halo = new Float32Array(N);

  // struct sampling (edges vs faces)
  const sampleBox = (i3, i) => {
    const bIdx = Math.floor(Math.random() * BOXES.length);
    const [w, h, d, cx, cy, cz] = BOXES[bIdx];
    const hw = w / 2;
    const hh = h / 2;
    const hd = d / 2;
    const isEdge = Math.random() < 0.55;
    let x;
    let y;
    let z;
    if (isEdge) {
      const axis = Math.floor(Math.random() * 3);
      const s1 = Math.random() < 0.5 ? -1 : 1;
      const s2 = Math.random() < 0.5 ? -1 : 1;
      const t = Math.random() * 2 - 1;
      if (axis === 0) {
        x = t * hw;
        y = s1 * hh;
        z = s2 * hd;
      } else if (axis === 1) {
        x = s1 * hw;
        y = t * hh;
        z = s2 * hd;
      } else {
        x = s1 * hw;
        y = s2 * hh;
        z = t * hd;
      }
    } else {
      const f = Math.floor(Math.random() * 6);
      x = (Math.random() - 0.5) * w;
      y = (Math.random() - 0.5) * h;
      z = (Math.random() - 0.5) * d;
      if (f === 0) x = hw;
      else if (f === 1) x = -hw;
      else if (f === 2) y = hh;
      else if (f === 3) y = -hh;
      else if (f === 4) z = hd;
      else z = -hd;
    }
    struct[i3] = cx + x + (Math.random() - 0.5) * 0.015;
    struct[i3 + 1] = cy + y + (Math.random() - 0.5) * 0.015;
    struct[i3 + 2] = cz + z + (Math.random() - 0.5) * 0.015;
    bright[i] = isEdge ? 1 : 0.32;
  };

  // --- the pixel website: precise & decorative, z = 1 plane ---
  // page x∈[-2.2,2.4] y∈[0.45,3.75]; shapes are weighted samplers
  const Z = 1.0;
  const siteShapes = [];
  const addShape = (w, c, b, a, fn) => siteShapes.push({ w, c, b, a, fn });
  const jz = () => Z + (Math.random() - 0.5) * 0.03;
  const sRect = (x0, x1, y0, y1, shear = 0) => (o, i3) => {
    const y = y0 + Math.random() * (y1 - y0);
    o[i3] = x0 + Math.random() * (x1 - x0) + (y - (y0 + y1) / 2) * shear;
    o[i3 + 1] = y;
    o[i3 + 2] = jz();
  };
  const sLine = (x0, y0, x1, y1, th) => (o, i3) => {
    const t = Math.random();
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy) || 1;
    const off = (Math.random() - 0.5) * th;
    o[i3] = x0 + dx * t + (-dy / len) * off;
    o[i3 + 1] = y0 + dy * t + (dx / len) * off;
    o[i3 + 2] = jz();
  };
  const sCircle = (cx, cy, r) => (o, i3) => {
    const a = Math.random() * Math.PI * 2;
    const rr2 = r * Math.sqrt(Math.random());
    o[i3] = cx + Math.cos(a) * rr2;
    o[i3 + 1] = cy + Math.sin(a) * rr2;
    o[i3 + 2] = jz();
  };
  const sRR = (x0, x1, y0, y1, r) => (o, i3) => {
    const w = x1 - x0 - 2 * r;
    const h = y1 - y0 - 2 * r;
    const arc = (Math.PI * r) / 2;
    const per = 2 * w + 2 * h + 4 * arc;
    let t = Math.random() * per;
    let x;
    let y;
    const corner = (cx2, cy2, a0, tt) => {
      const a = a0 + (tt / arc) * (Math.PI / 2);
      x = cx2 + Math.cos(a) * r;
      y = cy2 + Math.sin(a) * r;
    };
    if (t < w) {
      x = x0 + r + t;
      y = y0;
    } else if ((t -= w) < arc) corner(x1 - r, y0 + r, -Math.PI / 2, t);
    else if ((t -= arc) < h) {
      x = x1;
      y = y0 + r + t;
    } else if ((t -= h) < arc) corner(x1 - r, y1 - r, 0, t);
    else if ((t -= arc) < w) {
      x = x1 - r - t;
      y = y1;
    } else if ((t -= w) < arc) corner(x0 + r, y1 - r, Math.PI / 2, t);
    else if ((t -= arc) < h) {
      x = x0;
      y = y1 - r - t;
    } else corner(x0 + r, y0 + r, Math.PI, t - h);
    o[i3] = x + (Math.random() - 0.5) * 0.015;
    o[i3 + 1] = y + (Math.random() - 0.5) * 0.015;
    o[i3 + 2] = jz();
  };
  const sPillFill = (x0, x1, y0, y1) => (o, i3) => {
    const r = (y1 - y0) / 2;
    const cy = (y0 + y1) / 2;
    const y = y0 + Math.random() * (y1 - y0);
    const inset = r - Math.sqrt(Math.max(0, r * r - (y - cy) * (y - cy)));
    o[i3] = x0 + inset + Math.random() * (x1 - x0 - 2 * inset);
    o[i3 + 1] = y;
    o[i3 + 2] = jz();
  };

  // page frame + browser chrome (dots, url bar with padlock, divider)
  addShape(1700, COMP.FRAME, 0.9, 0, sRR(-2.2, 2.4, 0.45, 3.75, 0.14));
  addShape(60, COMP.FRAME, 1, 0, sCircle(-2.02, 3.6, 0.04));
  addShape(60, COMP.FRAME, 1, 0, sCircle(-1.88, 3.6, 0.04));
  addShape(60, COMP.FRAME, 1, 0, sCircle(-1.74, 3.6, 0.04));
  addShape(430, COMP.FRAME, 0.6, 0, sRR(-1.5, 0.9, 3.51, 3.69, 0.09));
  addShape(60, COMP.FRAME, 0.9, 0, sCircle(-1.38, 3.6, 0.03));
  addShape(240, COMP.FRAME, 0.55, 0, sLine(-2.2, 3.42, 2.4, 3.42, 0.012));
  // nav — round logo mark + wordmark, menu words, active underline, pill button
  addShape(240, COMP.NAV, 1, 0, sCircle(-1.95, 3.17, 0.085));
  addShape(420, COMP.NAV, 0.85, 0, sRect(-1.78, -1.32, 3.12, 3.22));
  addShape(230, COMP.NAV, 0.8, 0, sRect(0.32, 0.62, 3.13, 3.21));
  addShape(230, COMP.NAV, 0.8, 0, sRect(0.72, 1.06, 3.13, 3.21));
  addShape(230, COMP.NAV, 0.8, 0, sRect(1.16, 1.4, 3.13, 3.21));
  addShape(140, COMP.NAV, 1, 1, sLine(0.32, 3.05, 0.62, 3.05, 0.02));
  addShape(650, COMP.NAV, 1, 0, sRR(1.54, 2.26, 3.04, 3.3, 0.13));
  // headline — words, one sheared lime word reading as the serif italic
  addShape(700, COMP.HERO, 0.85, 0, sRect(-2.0, -1.25, 2.52, 2.82));
  addShape(560, COMP.HERO, 0.85, 0, sRect(-1.1, -0.5, 2.52, 2.82));
  addShape(800, COMP.HERO, 1, 1, sRect(-0.34, 0.52, 2.46, 2.88, 0.22));
  addShape(520, COMP.HERO, 0.85, 0, sRect(0.68, 1.32, 2.52, 2.82));
  addShape(620, COMP.HERO, 0.85, 0, sRect(-2.0, -1.3, 2.14, 2.44));
  addShape(560, COMP.HERO, 0.85, 0, sRect(-1.16, -0.52, 2.14, 2.44));
  addShape(480, COMP.HERO, 0.85, 0, sRect(-0.38, 0.7, 2.14, 2.44));
  addShape(260, COMP.HERO, 0.5, 0, sRect(-2.0, -1.15, 1.95, 2.02));
  addShape(200, COMP.HERO, 0.5, 0, sRect(-1.05, -0.35, 1.95, 2.02));
  // CTA — accent pill with a particle arrow + underlined text link
  addShape(1450, COMP.CTA, 1, 1, sPillFill(-2.0, -1.02, 1.55, 1.87));
  addShape(120, COMP.CTA, 1, 0, sLine(-1.36, 1.71, -1.18, 1.71, 0.022));
  addShape(90, COMP.CTA, 1, 0, sLine(-1.18, 1.71, -1.27, 1.79, 0.022));
  addShape(90, COMP.CTA, 1, 0, sLine(-1.18, 1.71, -1.27, 1.63, 0.022));
  addShape(300, COMP.CTA, 0.8, 0, sRect(-0.7, -0.12, 1.65, 1.75));
  addShape(150, COMP.CTA, 1, 1, sLine(-0.7, 1.56, -0.12, 1.56, 0.018));
  // hero media block — placeholder frame with ridge-line + sun
  addShape(820, COMP.HERO, 0.9, 0, sRR(0.95, 2.15, 1.5, 2.86, 0.1));
  addShape(260, COMP.HERO, 0.7, 0, sLine(1.0, 1.72, 1.42, 2.28, 0.02));
  addShape(200, COMP.HERO, 0.7, 0, sLine(1.42, 2.28, 1.66, 2.02, 0.02));
  addShape(240, COMP.HERO, 0.7, 0, sLine(1.66, 2.02, 2.1, 2.6, 0.02));
  addShape(130, COMP.HERO, 1, 1, sCircle(1.86, 2.62, 0.07));
  // three cards — rounded frames, icon tiles, title + ragged body lines
  [[-2.05, -0.75], [-0.55, 0.75], [0.95, 2.25]].forEach(([x0, x1]) => {
    addShape(640, COMP.CARDS, 0.9, 0, sRR(x0, x1, 0.6, 1.34, 0.09));
    addShape(170, COMP.CARDS, 1, 0, sRR(x0 + 0.14, x0 + 0.36, 1.04, 1.24, 0.05));
    addShape(190, COMP.CARDS, 0.85, 0, sRect(x0 + 0.14, x0 + 0.68, 0.86, 0.94));
    addShape(150, COMP.CARDS, 0.55, 0, sRect(x0 + 0.14, x1 - 0.2, 0.72, 0.77));
    addShape(130, COMP.CARDS, 0.55, 0, sRect(x0 + 0.14, x1 - 0.5, 0.62, 0.67));
  });
  // ambient dust behind the page
  addShape(3600, COMP.AMBIENT, 0.25, 0, (o, i3) => {
    o[i3] = 0.1 + (Math.random() - 0.5) * 7.5;
    o[i3 + 1] = 2.1 + (Math.random() - 0.5) * 5.2;
    o[i3 + 2] = 0.1 + Math.random() * 0.6;
  });
  const siteTotalW = siteShapes.reduce((s2, sh) => s2 + sh.w, 0);
  const sampleSite = (i3, i) => {
    let t = Math.random() * siteTotalW;
    let sh = siteShapes[siteShapes.length - 1];
    for (let k = 0; k < siteShapes.length; k++) {
      t -= siteShapes[k].w;
      if (t <= 0) {
        sh = siteShapes[k];
        break;
      }
    }
    sh.fn(site, i3);
    comp[i] = sh.c;
    brightSite[i] = sh.b;
    acc[i] = sh.a;
  };

  for (let i = 0; i < N; i++) {
    const i3 = i * 3;
    const r = 3.5 + Math.random() * 6.5;
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    chaos[i3] = r * Math.sin(ph) * Math.cos(th);
    chaos[i3 + 1] = r * Math.cos(ph) * 0.7;
    chaos[i3 + 2] = r * Math.sin(ph) * Math.sin(th);

    const gx = Math.round(((Math.random() - 0.5) * 14) / 0.3) * 0.3;
    const gz = Math.round(((Math.random() - 0.5) * 14) / 0.3) * 0.3;
    grid[i3] = gx + (Math.random() - 0.5) * 0.05;
    grid[i3 + 1] = -1 + (Math.random() - 0.5) * 0.02;
    grid[i3 + 2] = gz + (Math.random() - 0.5) * 0.05;

    sampleBox(i3, i);
    sampleSite(i3, i);

    const ang = Math.random() * Math.PI * 2;
    const rr = 4.3 + (Math.random() - 0.5) * 0.65;
    ring[i3] = Math.cos(ang) * rr;
    ring[i3 + 1] = Math.sin(ang) * rr * 0.92;
    ring[i3 + 2] = (Math.random() - 0.5) * 0.6;

    seeds[i] = Math.random();
    sizes[i] = 0.7 + Math.random() * 0.7;
    halo[i] = i % 55 === 0 ? 1 : 0;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(chaos, 3));
  geo.setAttribute('aGrid', new THREE.BufferAttribute(grid, 3));
  geo.setAttribute('aStruct', new THREE.BufferAttribute(struct, 3));
  geo.setAttribute('aSite', new THREE.BufferAttribute(site, 3));
  geo.setAttribute('aRing', new THREE.BufferAttribute(ring, 3));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
  geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
  geo.setAttribute('aBright', new THREE.BufferAttribute(bright, 1));
  geo.setAttribute('aBrightSite', new THREE.BufferAttribute(brightSite, 1));
  geo.setAttribute('aAcc', new THREE.BufferAttribute(acc, 1));
  geo.setAttribute('aComp', new THREE.BufferAttribute(comp, 1));
  geo.setAttribute('aHalo', new THREE.BufferAttribute(halo, 1));

  const mat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uTime: { value: 0 },
      uSize: { value: 0.135 },
      uVel: { value: 0 },
      uGather: { value: 0 },
      uGrid: { value: 0 },
      uStruct: { value: 0 },
      uSite: { value: 0 },
      uRecede: { value: 0 },
      uRing: { value: 0 },
      uRipple: { value: 0 },
      uEmber: { value: 0 },
      uHalo: { value: 0 },
      uFocusComp: { value: -1 },
      uFocusAmt: { value: 0 },
      uAnchor: { value: new THREE.Vector3(0, 0.6, 8) },
      uColor: { value: GREY.clone() },
      uAccent: { value: THEMES.dark.accent.clone() },
      uOpacity: { value: 0.6 },
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  scene.add(points);

  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.6, 0.75, 0.14);
  composer.addPass(bloom);

  const target = { hero: 0, stage: 0, services: 0, cta: 0, vel: 0 };
  const cur = { story: 0, vel: 0, px: 0, py: 0, hero: 0 };
  const pointer = { x: 0, y: 0 };
  const anchorNdc = { x: 0, y: 0, set: false };
  const anchorVec = new THREE.Vector3();
  const camPos = new THREE.Vector3();
  const camLook = new THREE.Vector3();
  const colA = new THREE.Color();
  let theme = THEMES.dark;

  function setTheme(name) {
    theme = THEMES[name] || THEMES.dark;
    scene.background = new THREE.Color(theme.bg);
    scene.fog.color.set(theme.bg);
    mat.blending = theme.blending;
    mat.needsUpdate = true;
    mat.uniforms.uSize.value = 0.135 * theme.size;
    mat.uniforms.uAccent.value.copy(theme.accent);
    bloom.threshold = theme.threshold;
  }

  function camAt(prog) {
    let a = CAM[0];
    let b = CAM[CAM.length - 1];
    for (let i = 0; i < CAM.length - 1; i++) {
      if (prog >= CAM[i].p && prog <= CAM[i + 1].p) {
        a = CAM[i];
        b = CAM[i + 1];
        break;
      }
    }
    if (prog > CAM[CAM.length - 1].p) a = b = CAM[CAM.length - 1];
    const t = a === b ? 1 : ease(seg(prog, a.p, b.p));
    camPos.set(lerp(a.pos[0], b.pos[0], t), lerp(a.pos[1], b.pos[1], t), lerp(a.pos[2], b.pos[2], t));
    camLook.set(lerp(a.look[0], b.look[0], t), lerp(a.look[1], b.look[1], t), lerp(a.look[2], b.look[2], t));
  }

  function setState(next) {
    Object.assign(target, next);
  }

  function setPointer(nx, ny) {
    pointer.x = nx;
    pointer.y = ny;
  }

  function setAnchor(nx, ny) {
    anchorNdc.x = nx;
    anchorNdc.y = ny;
    anchorNdc.set = true;
  }

  const compVec = new THREE.Vector3();
  function getCompRect(idx) {
    const rc = COMP_RECTS[idx];
    if (!rc) return null;
    const cw = canvas.clientWidth || 1;
    const ch = canvas.clientHeight || 1;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let ok = true;
    for (let sx = 0; sx <= 1; sx++) {
      for (let sy = 0; sy <= 1; sy++) {
        compVec.set(sx ? rc.x1 : rc.x0, sy ? rc.y1 : rc.y0, 1.0).project(camera);
        if (compVec.z > 1 || compVec.z < -1) ok = false;
        const px = (compVec.x * 0.5 + 0.5) * cw;
        const py = (-compVec.y * 0.5 + 0.5) * ch;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
      }
    }
    return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, w: maxX - minX, h: maxY - minY, valid: ok };
  }

  function resize() {
    const w = canvas.clientWidth || 1;
    const h = canvas.clientHeight || 1;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  function render(timeSec) {
    let raw = 0.1 * target.hero;
    if (target.stage > 0) raw = Math.max(raw, 0.1 + 0.68 * target.stage);
    if (target.services > 0) raw = Math.max(raw, 0.78 + 0.12 * target.services);
    if (target.cta > 0) raw = Math.max(raw, 0.9 + 0.1 * target.cta);

    cur.story += (raw - cur.story) * 0.075;
    cur.vel += (target.vel - cur.vel) * 0.08;
    cur.hero += (target.hero - cur.hero) * 0.06;
    cur.px += (pointer.x - cur.px) * 0.055;
    cur.py += (pointer.y - cur.py) * 0.055;
    const story = cur.story;

    const tGrid = ease(seg(story, 0.155, 0.265));
    const tStruct = ease(seg(story, 0.28, 0.4));
    const tSite = ease(seg(story, 0.44, 0.522));
    const tRecede = ease(seg(story, 0.782, 0.85));
    const tRing = ease(seg(story, 0.9, 1));
    const gridBell = ease(seg(story, 0.165, 0.245)) * (1 - ease(seg(story, 0.28, 0.37)));

    // the tour: which component is under the lens
    let focusComp = -1;
    let focusAmt = 0;
    for (let i = 0; i < TOUR_STOPS.length; i++) {
      const st = TOUR_STOPS[i];
      const local = seg(story, st.s, st.s + st.w);
      if (local > 0 && local < 1) {
        focusComp = st.comp;
        focusAmt = ease(seg(local, 0, 0.3)) * (1 - ease(seg(local, 0.78, 1)));
        break;
      }
    }

    const u = mat.uniforms;
    u.uTime.value = timeSec;
    u.uVel.value = cur.vel;
    u.uGather.value = cur.hero * (1 - tGrid);
    u.uGrid.value = tGrid;
    u.uStruct.value = tStruct;
    u.uSite.value = tSite;
    u.uRecede.value = tRecede;
    u.uRing.value = tRing;
    u.uRipple.value = tGrid * (1 - tStruct);
    u.uEmber.value = tRecede * (1 - tRing);
    u.uFocusComp.value = focusComp;
    u.uFocusAmt.value = focusAmt;

    colA.copy(theme.base).lerp(theme.accent, 0.6 * tGrid + 0.4 * tStruct - 0.55 * tSite);
    colA.lerp(theme.soft, 0.3 * tRing);
    u.uColor.value.copy(colA);
    u.uOpacity.value = (0.62 + 0.3 * tGrid - 0.52 * tRecede + 0.44 * tRing) * theme.opacity;

    bloom.strength =
      Math.min(
        1.7,
        0.62 +
          0.5 * gridBell +
          0.25 * tStruct * (1 - tSite) +
          0.3 * tSite * (1 - tRecede) +
          0.3 * focusAmt +
          0.45 * tRing +
          0.22 * cur.vel
      ) * theme.bloom;

    camAt(story);
    // portrait viewports pull the camera back so compositions fit the width
    const wide = Math.max(1, 1.3 / camera.aspect);
    camPos.sub(camLook).multiplyScalar(wide).add(camLook);
    // pointer parallax eases off during the tour so close-ups stay framed
    const par = 1 - 0.7 * tSite * (1 - tRecede);
    camera.position.set(camPos.x + cur.px * 0.55 * par, camPos.y + cur.py * 0.35 * par, camPos.z);
    camera.lookAt(camLook);

    // the word-halo opens the page (around "purpose") and closes it (around
    // "vision"), pulling its particles from the ring at the finale
    u.uHalo.value = anchorNdc.set ? Math.max(1 - tGrid, tRing) : 0;
    if (anchorNdc.set && u.uHalo.value > 0.001) {
      anchorVec.set(anchorNdc.x, anchorNdc.y, 0.5).unproject(camera);
      anchorVec.sub(camera.position).normalize();
      u.uAnchor.value.copy(camera.position).addScaledVector(anchorVec, 10.5);
    }

    composer.render();
  }

  function dispose() {
    geo.dispose();
    mat.dispose();
    composer.dispose();
    renderer.dispose();
  }

  resize();
  return { setState, setPointer, setAnchor, setTheme, getCompRect, resize, render, dispose };
}
